import React from "react";
import { HeartPulse, ShieldAlert, History, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import arthereumProvider from "../../backend/ArthereumProvider";
import { actions } from "../redux";
import useThemeHook from "../hooks/useThemeHook";

const StreakResurrectionCard: React.FC = () => {
  const theme = useThemeHook();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.actualUser);
  const user = userData?.user;

  // Logic: Display if streak is 0 AND lastMiningDate exists AND it was recently lost
  // For demo/simplicity, we'll check if lastMiningDate is older than 48h but newer than 7 days
  const lastMining = user?.lastMiningDate ? new Date(user.lastMiningDate.seconds * 1000) : null;
  const isStreakLost = user?.currentStreak === 0 && lastMining && (new Date().getTime() - lastMining.getTime() > 48 * 3600000);
  
  // We'll mock a "previous streak" for now or use a field if available
  // Let's assume there's a 'maxStreak' or just mock 10 for the UI demonstration if we don't have the real history
  const previousStreak = user?.maxStreak || 12; 
  const piCost = Math.max(10, (Math.floor(previousStreak / 10) + 1) * 10);

  const handleResurrect = async () => {
    if (!user?.id) return;
    if (user.picoinBalance < piCost) {
      alert("Insufficient Pi balance to save your streak!");
      return;
    }

    try {
      await arthereumProvider.resurrectStreak(user.id, previousStreak, piCost);
      // Update Redux locally instead of re-fetching from Firestore
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          picoinBalance: (user.picoinBalance || 0) - piCost,
          currentStreak: previousStreak,
        },
      }));
      alert(`Streak Resurrected! Your ${previousStreak} day fire is back! 🔥❤️‍🩹`);
    } catch (error: any) {
      alert(error.message || "Resurrection failed");
    }
  };

  if (!isStreakLost) return null;

  return (
    <motion.div 
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className={`mx-4 mt-6 p-5 rounded-3xl border-2 overflow-hidden relative ${
        theme === "dark" ? "bg-red-900/20 border-red-500/30" : "bg-red-50 border-red-100"
      }`}
    >
      <div className="flex items-start gap-4 relative z-10">
        <div className="bg-red-500 p-3 rounded-2xl shadow-lg shadow-red-500/20 animate-pulse">
          <HeartPulse size={24} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`text-sm font-black uppercase tracking-tight ${theme === "dark" ? "text-white" : "text-red-900"}`}>
              Streak Lost!
            </h3>
            <span className="bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase italic">
              24h LEFT
            </span>
          </div>
          <p className={`text-[11px] font-bold leading-tight ${theme === "dark" ? "text-slate-300" : "text-red-700"}`}>
            Your <span className="underline decoration-2">{previousStreak} day streak</span> has died. Don't let your hard work vanish forever!
          </p>
          
          <button 
            onClick={handleResurrect}
            className="mt-4 w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 active:scale-95 transition-all"
          >
            RESTORE FOR {piCost} PI
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Background Icon */}
      <History size={80} className="absolute -bottom-4 -right-4 text-red-500/10 rotate-12" />
    </motion.div>
  );
};

export default StreakResurrectionCard;
